/**
 * The Cortex Console Git Checkouts blade (harness row CC3B-05; spec.md 8A
 * ViewGitCheckouts, design.md sections 3, 4 and 11).
 *
 * What this component does: render the answer
 * `GET /api/cortex/console/git-checkouts` gave - one section per group, in the order
 * the answer gives them, each headed by `sponsor_label`, each checkout inside it as a
 * card titled by `checkout_display_name`.
 *
 * What it deliberately does NOT do:
 *
 * - bucket, sort or fall back a group of its own (forbidden path
 *   `client_side_grouping_by_operator`). Grouping happened once, server-side, in
 *   `build_git_checkout_groups()`; this blade maps over `groups` in the order given;
 * - title a card by `machine_id` (forbidden path `checkout_title_from_machine_id`).
 *   `machine_id` is a body meta line, the same place `branch` and `liveness` sit;
 * - start, stop or apply anything. There is no remediation control here at all,
 *   matching Instances.
 */
import React from "react";
import { ContentRegionLoader } from "../components/loading";
import type {
  ConsoleGitCheckoutGroup,
  ConsoleGitCheckoutRow,
  ConsoleGitCheckoutsResponse,
  ConsoleRefusal,
} from "../api/types";
import styles from "./GitCheckoutsBlade.module.css";

const LIVENESS_LABELS: Record<string, string> = {
  live: "Live",
  silent: "Silent",
  abandoned: "Abandoned",
};

const WORKTREES_MARKER = "/.worktrees/";

function humanise(id: string): string {
  const words = id.replace(/_/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : id;
}

export function livenessLabel(liveness: string): string {
  return LIVENESS_LABELS[liveness] || humanise(liveness);
}

/**
 * The short name a person reads for a git checkout path. After `.worktrees/` when
 * that folder is present, otherwise the last folder. Works for Windows and cloud
 * paths; it does not assume `/workspaces/Cordexa.ai/`. Moved here from
 * `InstancesBlade.tsx` (CC-3B, CC3B-06): the server now echoes its own computed
 * `checkout_display_name` for card titles, so this blade is this function's one
 * remaining home rather than a second copy.
 */
export function checkoutDisplayName(path: string | null | undefined): string {
  const text = (path || "").trim();
  if (!text) {
    return "not reported";
  }
  const normalised = text.replace(/\\/g, "/");
  const markerAt = normalised.lastIndexOf(WORKTREES_MARKER);
  if (markerAt !== -1) {
    const rest = normalised.slice(markerAt + WORKTREES_MARKER.length).replace(/\/+$/, "");
    return rest || "not reported";
  }
  const parts = normalised.replace(/\/+$/, "").split("/").filter(Boolean);
  return parts[parts.length - 1] || "repo root";
}

/** A value the server did not report, said in words rather than left blank. */
export function reported(value: string | null | undefined): string {
  return value ? value : "not reported";
}

function slug(value: string | null | undefined, fallback: string): string {
  const text = (value || "").trim();
  return text ? text.replace(/[^a-zA-Z0-9_-]+/g, "-") : fallback;
}

export function isGitCheckouts(
  body: ConsoleGitCheckoutsResponse | ConsoleRefusal,
): body is ConsoleGitCheckoutsResponse {
  return (
    (body as ConsoleGitCheckoutsResponse).response_type === "console_git_checkouts"
  );
}

export type GitCheckoutsBladeProps = {
  gitCheckouts: ConsoleGitCheckoutsResponse | ConsoleRefusal | null;
  loading: boolean;
  onRefresh: () => void;
};

function MetaLine({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <p className={styles.cardLine}>
      <span className={styles.cardLabel}>{label}</span>
      <span className={styles.cardValue}>{children}</span>
    </p>
  );
}

function CheckoutCard({
  row,
  testId,
}: {
  row: ConsoleGitCheckoutRow;
  testId: string;
}) {
  const behind =
    row.behind_origin_tagd === null || row.behind_origin_tagd === undefined
      ? "not reported"
      : String(row.behind_origin_tagd);
  return (
    <article className={styles.card} data-testid={`git-checkouts-checkout-${testId}`}>
      <h4 className={styles.cardTitle}>{row.checkout_display_name}</h4>
      <MetaLine label="Machine">{reported(row.machine_id)}</MetaLine>
      <MetaLine label="Branch">{reported(row.branch)}</MetaLine>
      <MetaLine label="Behind">{behind}</MetaLine>
      <MetaLine label="Status">
        <span
          className={styles.liveness}
          data-testid={`git-checkouts-liveness-${testId}`}
        >
          {livenessLabel(row.liveness)}
        </span>
      </MetaLine>
    </article>
  );
}

function GroupSection({
  group,
  groupIndex,
}: {
  group: ConsoleGitCheckoutGroup;
  groupIndex: number;
}) {
  const groupTestId = slug(group.sponsor_label, String(groupIndex));
  return (
    <section
      className={styles.section}
      data-testid={`git-checkouts-group-${groupTestId}`}
    >
      <h3 className={styles.sectionTitle}>{group.sponsor_label}</h3>
      {group.checkouts.length === 0 ? (
        <p className={styles.emptySection}>No checkouts in this group.</p>
      ) : (
        <div className={styles.cardGrid}>
          {group.checkouts.map((row, index) => (
            <CheckoutCard
              key={`${row.machine_id || ""}:${row.worktree_path || index}`}
              row={row}
              testId={slug(
                `${groupTestId}-${row.machine_id || ""}-${row.worktree_path || ""}`,
                String(index),
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export function GitCheckoutsBlade({
  gitCheckouts,
  loading,
  onRefresh,
}: GitCheckoutsBladeProps) {
  const answered =
    gitCheckouts && isGitCheckouts(gitCheckouts) ? gitCheckouts : null;
  const message = gitCheckouts?.message;
  // A refusal that never reached the registry carries no ``groups`` at all
  // (undefined), never an empty list - so the empty-state sentence is shown only
  // once the read actually happened, the same way Instances only shows its
  // "Shared environments" section once ``shared`` is present.
  const groups = answered?.groups;

  return (
    <div className={styles.blade} data-testid="console-git-checkouts-blade">
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>Git Checkouts</h2>
          {answered?.scope_statement ? (
            <p
              className={styles.scopeStatement}
              data-testid="git-checkouts-scope-statement"
            >
              {answered.scope_statement}
            </p>
          ) : null}
        </div>
        <button
          type="button"
          className={styles.refresh}
          data-testid="git-checkouts-refresh"
          onClick={onRefresh}
        >
          Refresh
        </button>
      </div>

      {message ? (
        <p className={styles.message} data-testid="git-checkouts-message">
          {message}
        </p>
      ) : null}

      {loading ? (
        <ContentRegionLoader
          label="Loading Git Checkouts"
          data-testid="git-checkouts-loading"
        />
      ) : null}

      {!loading && answered && groups ? (
        groups.length === 0 ? (
          <p className={styles.emptySection} data-testid="git-checkouts-empty">
            No git checkouts are visible to you.
          </p>
        ) : (
          groups.map((group, index) => (
            <GroupSection
              key={group.sponsor_identity ?? "not-reported"}
              group={group}
              groupIndex={index}
            />
          ))
        )
      ) : null}
    </div>
  );
}
